import { ExportPriceRatingEnum, PriceEvaluationEnum } from 'config/enums'
import { isPrice } from 'utilities/format'

export const priceEvaluationSteps = [
  PriceEvaluationEnum.VERY_GOOD,
  PriceEvaluationEnum.GOOD,
  PriceEvaluationEnum.FAIR,
  PriceEvaluationEnum.INCREASED,
  PriceEvaluationEnum.HIGH,
]

export const priceEvaluationColors = {
  [PriceEvaluationEnum.VERY_GOOD]: 'priceEvaluationVeryGood',
  [PriceEvaluationEnum.GOOD]: 'priceEvaluationGood',
  [PriceEvaluationEnum.FAIR]: 'priceEvaluationFair',
  [PriceEvaluationEnum.INCREASED]: 'priceEvaluationIncreased',
  [PriceEvaluationEnum.HIGH]: 'priceEvaluationHigh',
}

// The price scale in the evaluation modal uses a lighter tone for the 4th segment
export const priceEvaluationScaleColors = {
  ...priceEvaluationColors,
  [PriceEvaluationEnum.INCREASED]: 'priceEvaluationScaleIncreased',
}

/**
 * Export price of a car, shown on the cards and compared by the evaluation.
 * The backend fills `exportprijs` (including VAT) or `exportprijs_exbtw`
 * (excluding VAT); `isExportPriceInclVat` tells which one is shown.
 */
export const getExportPrice = (source = {}) => {
  if (isPrice(source.exportprijs)) {
    return Number(source.exportprijs)
  }

  return isPrice(source.exportprijs_exbtw)
    ? Number(source.exportprijs_exbtw)
    : null
}

/** True when the export price comes from `exportprijs`, which includes VAT */
export const isExportPriceInclVat = (source = {}) => isPrice(source.exportprijs)

/**
 * Profit margin used on the car file summary and stock cards: selling price
 * minus purchase price. Returns null when there is no purchase price to
 * subtract, same as the summary which then hides the row.
 */
export const calculateMargin = (sellingPrice, purchasePrice) => {
  const hasPurchasePrice = isPrice(purchasePrice) && Number(purchasePrice) > 0

  return hasPurchasePrice && isPrice(sellingPrice)
    ? Number(sellingPrice) - Number(purchasePrice)
    : null
}

/**
 * Reads selling and purchase prices from an overview/carfile payload (Dutch
 * or mapped field names) and returns calculateMargin. `marge` from the list
 * API is not this value, so it is only used when the prices are missing.
 */
export const getMargin = (source = {}) => {
  const sellingPrice =
    source.verkoopprijs ?? source.sellingprice ?? source.sellingPrice
  const purchasePrice =
    source.inkoopprijs_view ?? source.inkoopprijs ?? source.purchaseprice

  const margin = calculateMargin(sellingPrice, purchasePrice)

  if (margin !== null) {
    return margin
  }

  return isPrice(source.marge) && Number(source.marge) !== 0
    ? Number(source.marge)
    : null
}

/** Turns a "4400 - 5700" range from the API into { min, max } */
const parseRange = (value) => {
  const [min, max] = String(value === undefined || value === null ? '' : value)
    .split('-')
    .map((part) => Number(part.trim()))

  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null
}

const rangeFields = [
  'very_good_price_range',
  'good_price_range',
  'reasonable_price_range',
  'increased_price_range',
  'high_price_range',
]

/**
 * Reads the response of the EuroStocks ranges endpoint: one price range per
 * category, the rating and the price that was evaluated.
 */
export const getPriceEvaluationRanges = (result) => {
  if (!result) {
    return null
  }

  const ranges = rangeFields.map((field) => parseRange(result[field]))

  if (ranges.some((range) => range === null)) {
    return null
  }

  return {
    ranges,
    evaluation: normalizePriceEvaluation(result.rating),
    price: isPrice(result.price) ? Number(result.price) : null,
  }
}

/**
 * Position of a price on the scale as a percentage (0-100). Every range takes
 * an equal part of the scale and the price is placed linearly inside its range.
 */
export const getPriceEvaluationPointerPosition = (price, ranges) => {
  if (
    !Array.isArray(ranges) ||
    ranges.length === 0 ||
    !Number.isFinite(price)
  ) {
    return null
  }

  const segment = 100 / ranges.length
  const index = ranges.findIndex(({ min, max }) => price >= min && price <= max)

  if (index === -1) {
    return price < ranges[0].min ? 0 : 100
  }

  const { min, max } = ranges[index]
  const fraction = max > min ? (price - min) / (max - min) : 0.5

  return (index + fraction) * segment
}

/**
 * Accepts the evaluation either as a PriceEvaluationEnum value or as a 1-based
 * step number (1 = very good price, 5 = high price). Returns null when unknown.
 */
export const normalizePriceEvaluation = (value) => {
  if (priceEvaluationSteps.includes(value)) {
    return value
  }

  if (typeof value === 'string' && value in ExportPriceRatingEnum) {
    return ExportPriceRatingEnum[value]
  }

  const step = Number(value)

  return Number.isInteger(step) && step >= 1 && step <= 5
    ? priceEvaluationSteps[step - 1]
    : null
}

/** False for empty and unknown values; NO_RATING shows the grey "no rating" indicator */
export const hasPriceEvaluation = (evaluation) =>
  (typeof evaluation === 'string' && evaluation in ExportPriceRatingEnum) ||
  normalizePriceEvaluation(evaluation) !== null

// The better the price, the more blocks are highlighted (5 for very good, 1 for high (bad) price)
export const getPriceEvaluationBlockCount = (evaluation) => {
  const index = priceEvaluationSteps.indexOf(evaluation)

  return index === -1 ? 0 : priceEvaluationSteps.length - index
}

/**
 * Stock days use the same 5 steps as the stock days filter and the management
 * info charts: 0-30, 31-60, 61-90, 91-120 and more than 120 days.
 */
export const stockDaysSteps = 5

export const getStockDaysStep = (days) => {
  const numberOfDays = Number(days) || 0

  return Math.min(Math.max(Math.ceil(numberOfDays / 30), 1), stockDaysSteps)
}

// UCC colours the steps green, gold and red; RDC uses its stadagen chart palette
export const getStockDaysColor = (days) =>
  `stockDaysStep${getStockDaysStep(days)}`
